import React from 'react';
import moment from 'moment';

import PediatricIndicatorIcon from '../../../../components/Icons/PediatricIndicatorIcon';
import SummaryPopover, {
  popoverCellStyle,
} from '../../../../components/SummaryPopover';
import {
  REVIEW_TYPES_REQUESTED,
  ROLES,
  XSOLIS_USER_TYPE,
} from '../../../../libs/Constants';
import { formatDate } from '../../../../libs/reactTableFormatters';
import Assignment from '../../../Assignment';
import { assignmentAccessor } from '../../accessors/assignmentAccessor';
import {
  dateOfBirthValueRenderer,
  MaxClsValueRenderer,
  MeLValueRenderer,
  PediatricIndicatorRenderer,
  TransferIndicatorRenderer,
} from '../../CellRendererComponents';
import { FormatLastPADate, Time } from '../../components/Time';
import { MEL_OPTIONS } from '../../constants/MeLValues.constants';
import { PEDIATRIC_INDICATOR_OPTIONS } from '../../constants/PediatricIndicatorValues.constants';
import {
  DEFAULT_TIME_FORMAT,
  GMT_TIME_FORMAT,
} from '../../constants/TimeCalculation.constants';
import { filterByDate, MeLFilterMethod } from '../../customFilters';
import { SelectColumnFilter } from '../../customFilters/filters/SelectFilter';
import QueueStatusIndicatorControl from '../../QueueStatusIndicator';
import ReviewType from '../../ReviewType';
import CountdownTimer from '../../timer';
import {
  getPediatricIndicator,
  sortByAssignment,
  sortByMeL,
  sortWithNullFirst,
} from '../index';
import { sortWithBooleanValues } from '../sortForBooleanValues';
import { getTotalLosColumnDef } from './getTotalLosColumnDef';

export const getXsolisColumns = (props) => {
  /*
    Make sure to add hidden columns to the end of the list to not mess
    up the rearrangement logic.  Sorry for the fragility :(
  */
  const defaults = [
    {
      id: 'sla_due_date',
      Header: 'TAT',
      accessor: 'sla_due_date',
      Cell: ({ row }) => {
        if (!row.sla_due_date) {
          return <span></span>;
        } else {
          return (
            <CountdownTimer
              start={row.sla_due_date}
              timezone={row.facilitytimezone}
            />
          );
        }
      },
      filterable: false,
      width: 75,
    },
    {
      id: 'is_pending',
      Header: 'PA Status',
      filterable: false,
      accessor: (d) => d.is_pending,
      Cell: ({ row }) => {
        return (
          <QueueStatusIndicatorControl row={row} username={props.username} />
        );
      },
      width: 100,
    },
    {
      Header: 'Assignment',
      id: 'assignedUsername',
      filterable: false,
      width: 235,
      style: { overflow: 'visible' },
      resizable: false,
      accessor: (d) => {
        return assignmentAccessor(
          d.assigned_username,
          props.reviewers,
          d.admission_id,
          d.entity_id
        );
      },
      sortMethod: sortByAssignment,
      Cell: ({ row }) => {
        return (
          <Assignment
            username={props.username}
            entityId={row.entityid}
            admissionId={row.admissionid}
            patientId={row.patientid}
            assignedUser={row.currentAssignment}
            reviewers={props.reviewers}
            isTeamLead={
              props.userRoles &&
              props.userRoles.find((r) => r.xpas_role_id === ROLES.TEAM_LEAD)
            }
            isManualReviewType={
              row.review_type_requested === REVIEW_TYPES_REQUESTED.MANUAL
            }
            isInternalSysAdmin={
              props.userRoles &&
              props.userRoles.find(
                (r) => r.xpas_role_id === ROLES.SYSTEM_ADMINISTRATOR
              ) &&
              props.userTypeId === XSOLIS_USER_TYPE
            }
            isExternalAdmin={
              props.userRoles &&
              props.userRoles.find(
                (r) => r.xpas_role_id === ROLES.ADMINISTRATOR
              ) &&
              props.userTypeId !== XSOLIS_USER_TYPE
            }
            isPAUser={
              props.userRoles &&
              props.userRoles.find(
                (r) => r.xpas_role_id === ROLES.PHYSICIAN_ADVISOR
              )
            }
            hasOpenPaReview={row.has_open_pa_review}
            patientName={row.patientName}
            style={{ maxWidth: '200px' }}
            assignmentCompleted={props.handleAssignmentComplete}
            includesEventQueue={props.includesEventQueue}
            queueState={props.state}
            setQueueState={props.setState}
            fetchQueue={props.fetchQueue}
          />
        );
      },
    },
    {
      id: 'patientName',
      Header: 'Patient',
      accessor: (d) => d.patient_name,
      Cell: ({ row }) => {
        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'left',
              cursor: 'pointer',
            }}
          >
            <div style={popoverCellStyle}>
              <SummaryPopover
                row={row}
                patientName={row.patientName}
                handleReviewsButton={props.handleReviewsButton}
                goToMessageFeed={props.goToMessageFeed}
                handleClose={props.handleClose}
                handleOpen={props.handleOpen}
              />
            </div>
          </span>
        );
      },
      width: 200,
    },
    {
      id: 'pediatricIndicator',
      width: 85,
      accessor: (d) => {
        return getPediatricIndicator(d.admission_date, d.birth_date);
      },
      sortable: true,
      sortMethod: sortWithBooleanValues,
      Header: <PediatricIndicatorIcon />,
      filterable: true,
      Filter: ({ filter, onChange }) => {
        return SelectColumnFilter(
          filter,
          PEDIATRIC_INDICATOR_OPTIONS,
          onChange
        );
      },
      Cell: PediatricIndicatorRenderer,
    },
    {
      id: 'birthDate',
      Header: 'DOB',
      accessor: 'birth_date',
      width: 120,
      Cell: dateOfBirthValueRenderer,
      filterMethod: filterByDate,
    },
    {
      id: 'accountNumber',
      Header: 'Account #',
      accessor: (d) => d.visit_nbr,
      width: 140,
    },
    {
      id: 'facility',
      Header: 'Facility',
      accessor: (d) => d.entity_name,
      filterMethod: (filter, row) => {
        const id = filter.pivotId || filter.id;
        return row[id] !== undefined && row[id] !== null
          ? String(String(row[id]).toLowerCase()).includes(
              filter.value.toLowerCase()
            )
          : false;
      },
      Cell: TransferIndicatorRenderer,
      width: 275,
    },
    {
      id: 'point_of_care_description',
      Header: 'Location',
      accessor: (l) => l.point_of_care_description,
      width: 200,
    },
    {
      id: 'room_nbr',
      accessor: 'room_nbr',
      Header: 'Room #',
      width: 150,
    },
    {
      id: 'admission_date',
      Header: 'Presentation Date',
      accessor: 'admission_date',
      Cell: (row) => <Time value={row.value} />,
      width: 150,
      filterable: false,
    },
    {
      id: 'client_escalation_date',
      Header: 'Escalation Date',
      accessor: 'client_escalation_date',
      Cell: ({ row }) => {
        if (!row.client_escalation_date) {
          return <span></span>;
        } else {
          return <Time value={row.client_escalation_date} />;
        }
      },
      width: 150,
      filterable: false,
    },
    {
      id: 'review_type_requested',
      Header: 'Review Type',
      filterable: false,
      width: 235,
      style: { overflow: 'visible' },
      resizable: false,
      accessor: (d) =>
        d.escalating_review_type_desc === 'Appeal/Retrospective'
          ? 'Appeal/Retrospective'
          : d.review_type_requested === 'Physician Advisor'
          ? 'Status Determination'
          : d.review_type_requested,
      Cell: ({ row, original }) => {
        if (original && original.manual_escalation) {
          return (
            <ReviewType
              data={original}
              state={props.state}
              setState={props.setState}
              userTypeId={props.userTypeId}
            />
          );
        } else {
          return row.review_type_requested;
        }
      },
    },
    {
      id: 'scheduled_date_time',
      Header: 'P2P Scheduled Date/Time',
      accessor: (d) => d.scheduled_date_time,
      width: 255,
      sortable: true,
      Filter: ({ filter, onChange }) => (
        <input
          onChange={(event) => onChange(event.target.value)}
          value={filter ? filter.value : ''}
          type='text'
          className='grid-filter-p2p-scheduled-datetime'
          style={{
            width: '100%',
          }}
        />
      ),
      Cell: ({ row }) => {
        //decode utc and facility time zone before displaying on PA work queue for DF only
        if (
          /[-]/.test(row.admission_id) &&
          row.scheduled_date_time &&
          row.facilitytimezone
        ) {
          const gmtDateTime = moment.utc(
            row.scheduled_date_time,
            GMT_TIME_FORMAT
          );
          const localDateTime = gmtDateTime
            .local()
            .tz(row.facilitytimezone)
            .format(DEFAULT_TIME_FORMAT);

          return localDateTime;
        } else {
          return <Time value={row.scheduled_date_time} />;
        }
      },
    },
    {
      id: 'review_due_date',
      accessor: 'review_due_date',
      Header: 'Review Due',
      Cell: ({ row }) => {
        if (row.sla_due_date) {
          return <Time value={row.review_due_date} />;
        } else if (row.peer_to_peer_deadline_date) {
          return formatDate({ value: row.review_due_date });
        }

        return <span></span>;
      },
      filterable: false,
      width: 130,
    },
    {
      id: 'second_level_reason_text',
      Header: '2nd Level Reason',
      accessor: (d) => d.second_level_reason_text,
      width: 160,
    },
    {
      id: 'attending_physician',
      Header: 'Attending Physician',
      accessor: 'attending_physician',
      width: 150,
    },
    {
      id: 'last_pa_review_user',
      accessor: 'last_pa_review_user',
      Header: 'Last PA Review',
      Cell: (row) => (
        <span>
          {row.original.last_pa_review_user}{' '}
          <FormatLastPADate value={row.original.last_pa_review_date} />
        </span>
      ),
      width: 300,
      filterable: false,
    },
    {
      id: 'primaryPayer',
      Header: 'Payer-Primary',
      accessor: (d) => d.financial_class,
      width: 240,
    },
    {
      id: 'secondaryPayer',
      Header: 'Secondary Payer',
      accessor: (d) => d.secondary_financial_class,
      width: 240,
    },
    {
      id: 'status',
      Header: 'Status',
      accessor: (d) => d.admission_status,
      width: 150,
    },
    {
      id: 'auto_escalation_reason_description',
      Header: 'Auto-Escalation Reason',
      accessor: 'auto_escalation_reason_description',
      width: 180,
      filterable: true,
    },
    {
      id: 'admission_id',
      Header: 'Admission Id',
      accessor: (d) => d.admission_id,
      width: 125,
      Filter: ({ filter, onChange }) => (
        <input
          onChange={(event) => onChange(event.target.value)}
          value={filter ? filter.value : ''}
          type='text'
          className='grid-filter-admission-id'
          style={{
            width: '100%',
          }}
        />
      ),
    },
    {
      id: 'mrn',
      accessor: 'mrn',
      Header: 'MRN',
      filterable: true,
      width: 125,
    },
    {
      id: 'chiefComplaint',
      accessor: 'chief_complaint',
      Header: 'Chief Complaint',
      width: 180,
    },
    {
      id: 'current_cls',
      accessor: 'current_cls',
      Header: 'Current CLS',
      filterable: false,
      sortable: true,
      sortMethod: sortWithNullFirst,
      width: 130,
    },
    {
      id: 'max_cls',
      accessor: 'max_cls',
      Header: 'Max CLS',
      filterable: false,
      sortable: true,
      width: 100,
      sortMethod: sortWithNullFirst,
      Cell: MaxClsValueRenderer,
    },
    getTotalLosColumnDef(props.enableLOSDecimal),
    {
      id: 'mel',
      accessor: 'mel',
      Header: 'MeL',
      filterable: true,
      width: 150,
      Filter: ({ filter, onChange }) => {
        return SelectColumnFilter(filter, MEL_OPTIONS, onChange);
      },
      filterMethod: MeLFilterMethod,
      sortable: true,
      sortMethod: sortByMeL,
      Cell: MeLValueRenderer,
    },
    {
      id: 'target_encounter_id',
      accessor: (d) => d.target_encounter_id,
      show: false,
      width: 0,
    },
    {
      id: 'entityid',
      accessor: (d) => d.entity_id,
      show: false,
      width: 0,
    },
    {
      id: 'patientid',
      accessor: (d) => d.patient_id,
      show: false,
      width: 0,
    },
    {
      id: 'admissionid',
      accessor: (d) => d.admission_id,
      show: false,
      width: 0,
    },
    {
      id: 'prioradmissionid',
      accessor: (d) => d.prior_admission_id,
      show: false,
      width: 0,
    },
    {
      id: 'manuallinkencounterpriorencounterid',
      accessor: (d) => d.manual_link_encounter_prior_encounter_id,
      show: false,
      width: 0,
    },
    {
      id: 'manuallinkencounternextencounterid',
      accessor: (d) => d.manual_link_encounter_next_encounter_id,
      show: false,
      width: 0,
    },
    {
      id: 'currentAssignment',
      Header: 'Current Assignment',
      accessor: (d) => d.assigned_username,
      show: false,
      width: 0,
    },
    {
      id: 'peer_to_peer_deadline_date',
      accessor: 'peer_to_peer_deadline_date',
      show: false,
      width: 0,
    },
    {
      id: 'schedule_option_selected',
      accessor: 'schedule_option_selected',
      show: false,
      width: 0,
    },
    {
      id: 'has_open_pa_review',
      accessor: 'has_open_pa_review',
      show: false,
      width: 0,
    },
    {
      id: 'facilitytimezone',
      accessor: (d) => {
        const { facilitiesTimezones } = props.state;
        if (facilitiesTimezones) {
          const rowTimezoneObject = facilitiesTimezones.find(
            (facility) => facility.ENTITY_ID === d.entity_id
          );
          return rowTimezoneObject && rowTimezoneObject.TIMEZONE;
        }
      },
      show: false,
      width: 0,
    },
  ];

  return defaults;
};
